export const CERT_ALIASES = {
  "200-301": "CCNA",
  "GCP-ACE": "ACE",
};

export const CERT_FALLBACKS = {
  "SAA-C03": {
    name: "AWS Certified Solutions Architect - Associate",
    provider: "AWS",
    level: "Associate",
    description: "Design resilient, secure, high-performing architectures on AWS.",
    tags: ["IAM", "VPC", "EC2", "S3", "High Availability", "Cost Optimization"],
    provider_color: "#FF9900",
  },
  "SY0-701": {
    name: "CompTIA Security+",
    provider: "CompTIA",
    level: "Entry",
    description: "Build practical cybersecurity foundations across threats, architecture, operations, and governance.",
    tags: ["Threats", "Identity", "Network Security", "Cryptography", "Risk", "Incident Response"],
    provider_color: "#0066B1",
  },
  "AZ-104": {
    name: "Microsoft Certified: Azure Administrator Associate",
    provider: "Microsoft",
    level: "Associate",
    description: "Manage Azure identity, governance, compute, storage, networking, and monitoring.",
    tags: ["Entra ID", "Governance", "Storage", "Virtual Machines", "Networking", "Monitoring"],
    provider_color: "#0078D4",
  },
  "CLF-C02": {
    name: "AWS Certified Cloud Practitioner",
    provider: "AWS",
    level: "Foundational",
    description: "Understand AWS cloud concepts, core services, security, architecture, pricing, and support.",
    tags: ["Cloud Concepts", "IAM", "EC2", "S3", "Billing", "Shared Responsibility"],
    provider_color: "#FF9900",
  },
  "AZ-900": {
    name: "Microsoft Certified: Azure Fundamentals",
    provider: "Microsoft",
    level: "Foundational",
    description: "Understand Azure cloud concepts, services, management, governance, and pricing.",
    tags: ["Cloud Concepts", "Azure Services", "Security", "Governance", "Pricing", "Support"],
    provider_color: "#0078D4",
  },
  "DVA-C02": {
    name: "AWS Certified Developer - Associate",
    provider: "AWS",
    level: "Associate",
    description: "Develop, deploy, secure, and troubleshoot cloud-based applications on AWS.",
    tags: ["Lambda", "DynamoDB", "API Gateway", "S3", "CloudWatch", "IAM"],
    provider_color: "#FF9900",
  },
  ACE: {
    name: "Google Associate Cloud Engineer",
    provider: "Google Cloud",
    level: "Associate",
    description: "Deploy, monitor, and manage Google Cloud solutions.",
    tags: ["Compute Engine", "IAM", "Cloud Storage", "VPC", "GKE", "Monitoring"],
    provider_color: "#4285F4",
  },
  CCNA: {
    name: "Cisco Certified Network Associate",
    provider: "Cisco",
    level: "Associate",
    description: "Install, operate, secure, and troubleshoot modern routed and switched networks.",
    tags: ["IP Connectivity", "VLANs", "Routing", "Wireless", "Security", "Automation"],
    provider_color: "#1BA0D7",
  },
  "AIF-C01": {
    name: "AWS Certified AI Practitioner",
    provider: "AWS",
    level: "Foundational",
    description: "Understand AI, ML, generative AI, responsible AI, and AWS AI services.",
    tags: ["AI Concepts", "Machine Learning", "Bedrock", "Responsible AI", "Security", "Use Cases"],
    provider_color: "#FF9900",
  },
  "ANS-C01": {
    name: "AWS Certified Advanced Networking - Specialty",
    provider: "AWS",
    level: "Specialty",
    description: "Design, implement, secure, operate, and troubleshoot complex AWS and hybrid network architectures.",
    tags: ["VPC", "Hybrid Connectivity", "Route 53", "Transit Gateway", "Network Security", "Troubleshooting"],
    provider_color: "#FF9900",
  },
  "DEA-C01": {
    name: "AWS Certified Data Engineer - Associate",
    provider: "AWS",
    level: "Associate",
    description: "Build, secure, monitor, and optimize AWS data pipelines, storage, transformation, and governance workflows.",
    tags: ["Data Pipelines", "Glue", "Redshift", "Kinesis", "Lake Formation", "Data Quality"],
    provider_color: "#FF9900",
  },
  "SOA-C03": {
    name: "AWS Certified CloudOps Engineer - Associate",
    provider: "AWS",
    level: "Associate",
    description: "Deploy, manage, operate, monitor, and troubleshoot AWS workloads.",
    tags: ["Monitoring", "Networking", "Compute", "Storage", "Automation", "Resilience"],
    provider_color: "#FF9900",
  },
};

export const normalizeCertCode = (code = "") => {
  const cleaned = String(code || "").trim().toUpperCase();
  return CERT_ALIASES[cleaned] || cleaned;
};

export const configCertCode = (config = {}, key = "") => normalizeCertCode(
  config.code || config.id || config.cert_code || key
);

// A fulfilled catalogue response is authoritative, including the case where it
// contains no active certifications. Static display metadata never decides
// whether a certification is offered.
export const activeExamConfigs = (configs = {}) => Object.entries(configs || {})
  .reduce((out, [key, config]) => {
    if (!config || config.is_active === false || config.active === false) return out;
    const code = configCertCode(config, key);
    if (!code) return out;
    out[code] = { ...config, id: code, code };
    return out;
  }, {});

export const friendlyProviderName = (provider = "") =>
  provider === "Google Cloud" ? "Google Cloud" : provider || "Certification";

export const certificationNameWithCode = (name = "", code = "") => {
  const cleanName = String(name || "").trim();
  const normalizedCode = normalizeCertCode(code);
  if (!cleanName) return normalizedCode;
  if (!normalizedCode) return cleanName;

  const escapedCode = normalizedCode.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const alreadyIncludesCode = new RegExp(
    `(^|[^A-Z0-9])${escapedCode}([^A-Z0-9]|$)`,
    "i"
  ).test(cleanName);
  return alreadyIncludesCode ? cleanName : `${cleanName} (${normalizedCode})`;
};

export const mergeCertData = (code, metadata = {}, config = {}, count = null) => {
  const normalized = normalizeCertCode(code);
  const fallback = CERT_FALLBACKS[normalized] || {};
  return {
    ...fallback,
    ...metadata,
    ...config,
    code: normalized,
    name: config.name || metadata.name || fallback.name || normalized,
    provider: config.provider || config.vendor || metadata.provider || fallback.provider || "",
    level: config.category || metadata.level || fallback.level || "",
    description: config.description || metadata.description || fallback.description || "",
    tags: config.tags || metadata.tags || fallback.tags || [],
    provider_color: config.provider_color || metadata.provider_color || fallback.provider_color || "#3b82f6",
    questionCount: count ?? config.questionCount ?? null,
    domains: config.domains || [],
    examQuestions: config.questionCount || null,
    timeMinutes: config.timeMinutes || null,
    passingScore: config.passingScore || null,
    passingScoreScale: config.passingScoreScale || null,
    passingScoreBasis: config.passingScoreBasis || null,
    officialExam: config.officialExam || null,
  };
};

// Lowercase + trailing slash is the FINAL served form of public URLs
// (for example /certifications/clf-c02/). Emitting that
// form directly means crawled hrefs, canonicals, and the sitemap all agree
// and never point at a redirect. Cert-code LOOKUPS stay uppercase via
// normalizeCertCode — only the URL casing changes here.
export const certPath = (code) => `/certifications/${encodeURIComponent(normalizeCertCode(code)).toLowerCase()}/`;
